import { InputLabel } from '@mui/material';
import type { FC } from 'react';
import { useCallback } from 'react';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { TeamingReason } from 'features/teaming/teaming-base/enums';
import { useAppDispatch } from 'features/app/hooks';
import { handleError } from 'features/notification/store';
import { TeamingAdhocRequestTextField } from 'features/teaming/teaming-adhoc/components';
import { SelectTeamingLocation } from 'features/teaming/teaming-base/components';
import type { TeamRequestFormOptions } from 'features/teaming/teaming-base/interfaces';
import {
  requestAdhocTeaming,
  setTeamingReason,
} from 'features/teaming/teaming-base/store';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import type { CustomSelectItem } from 'features/ui/elements';
import { Button } from 'features/ui/elements';

interface AdhocTeamRequestFormProps {
  control: Control<TeamRequestFormOptions>;
  isValid: boolean;
  isLoading: boolean;
  closePopover: () => void;
  getFormValues: () => TeamRequestFormOptions;
  setLoading: (loading: boolean) => void;
  errors: FieldErrors<TeamRequestFormOptions>;
  locationOptions: CustomSelectItem[];
}

export const TeamingAdhocRequestForm: FC<AdhocTeamRequestFormProps> = ({
  control,
  isValid,
  isLoading,
  closePopover,
  getFormValues,
  setLoading,
  errors,
  locationOptions,
}) => {
  const dispatch = useAppDispatch();
  const { t: ct } = useTranslation();
  const { t } = useTranslation('translation', {
    keyPrefix: 'adhocTeamRequest',
  });
  const isUserNameError = Boolean(errors.adhocUsername);
  const userNameErrorMessage = errors.adhocUsername?.message;

  const handleStartTeaming = useCallback(
    async () => {
      setLoading(true);
      try {
        const formOptions = getFormValues();
        dispatch(setTeamingReason(formOptions.reason as TeamingReason));
        await dispatch(requestAdhocTeaming(formOptions)).unwrap();
      } catch (error) {
        dispatch(
          handleError({
            error,
            methodName: 'handleStartTeaming',
          })
        );
      } finally {
        setLoading(false);
        closePopover();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closePopover]
  );

  return (
    <>
      <TopLabel>{t('teamingLocation')}</TopLabel>
      <Controller
        name='location'
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange } }) => (
          <SelectTeamingLocation
            options={locationOptions}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <Label htmlFor={t('requestUserNameInput')}>{t('requestUserName')}</Label>
      <Controller
        name='adhocUsername'
        control={control}
        rules={{
          required: { value: true, message: t('userNameErrorMessage') },
        }}
        render={({ field: { value, onChange } }) => (
          <TeamingAdhocRequestTextField
            id={t('requestUserNameInput')}
            data-testid='teaming-adhoc-username-input'
            value={value}
            onChange={onChange}
            error={isUserNameError}
            helperText={userNameErrorMessage}
          />
        )}
      />
      <SubmitButton
        color='primary'
        size='small'
        fullWidth
        title={t('startTeaming')}
        autoFocus
        loading={isLoading}
        disabled={!isValid}
        onClick={handleStartTeaming}
      >
        {t('startTeaming')}
      </SubmitButton>
      <CancelButton
        color='secondary'
        size='small'
        fullWidth
        disabled={isLoading}
        title={ct('cancel')}
        onClick={closePopover}
      >
        {ct('cancel')}
      </CancelButton>
    </>
  );
};

const TopLabel = styled(InputLabel)`
  margin-bottom: ${theme.spacing(3)};
`;

const Label = styled(TopLabel)`
  margin-top: ${theme.spacing(8)};
`;

const SubmitButton = styled(Button)`
  margin-top: ${theme.spacing(8)};
`;

const CancelButton = styled(Button)`
  margin-top: ${theme.spacing(4)};
`;
